import React from 'react';
import type { BarberGrowthSnapshot } from '../../services/barberDataService';
import { commissionForBarber } from '../../shared/growth';

/**
 * Piece 2 — Weekly earnings breakdown: sliding-scale commission + Growth bonus + retail spiff → total.
 * Reads the persisted snapshot ($ fields computed at recompute). Null-safe. Finance tab of the profile.
 * BEHAVIOR-PIN: SLIK-GROWTH-SYSTEM-BLUEPRINT.md · commissionForRevenue (cliff) in shared/growth.ts.
 */
const money = (n: number) => `$${(Math.round(n * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const GrowthEarningsCard: React.FC<{ growth?: BarberGrowthSnapshot | null; retailSpiffPct?: number; commissionMode?: 'sliding' | 'flat'; commissionRate?: number }> = ({ growth, retailSpiffPct = 17.5, commissionMode, commissionRate }) => {
  if (!growth) return null;
  const rev = growth.weeklyServiceRevenue || 0;
  // Per-barber pay mode: flat % or the shop sliding cliff — same resolver payroll uses. Jose 2026-09-01.
  const c = commissionForBarber({ commissionMode, commissionRate }, rev, growth.commissionTiers?.length ? growth.commissionTiers : undefined);
  const bonus = growth.bonusEstWeekly || 0;
  const retail = growth.weeklyRetailDollars || 0;
  const spiff = Math.round(retail * (retailSpiffPct / 100) * 100) / 100;
  const total = Math.round((c.commission + bonus + spiff) * 100) / 100;

  const Row = ({ label, val, sub, strong }: { label: string; val: string; sub?: string; strong?: boolean }) => (
    <div className={`flex items-center justify-between py-1.5 ${strong ? 'border-t border-gray-200 dark:border-gray-700 mt-1 pt-2 font-bold' : ''}`}>
      <span className="text-gray-700 dark:text-gray-300">{label}{sub && <span className="text-xs text-gray-400 ml-1">{sub}</span>}</span>
      <span className={strong ? 'text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-200'}>{val}</span>
    </div>
  );

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">💵 Weekly earnings (est.)</h3>
      <Row label="Commission" sub={`${Math.round(c.rate * 100)}% ${c.flat ? 'flat' : 'tier'} · ${money(rev)} services`} val={money(c.commission)} />
      <Row label="Growth bonus" sub={growth.bonusPct > 0 ? `${growth.bonusPct}% · ${growth.tier}` : '—'} val={`+${money(bonus)}`} />
      <Row label="Retail spiff" sub={`${retailSpiffPct}% of ${money(retail)}`} val={`+${money(spiff)}`} />
      <Row label="Total" val={money(total)} strong />
      {c.nextTierAt != null && c.toNextTier != null && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Next commission tier at {money(c.nextTierAt)} — {money(c.toNextTier)} to go this week.</p>
      )}
      <p className="text-[11px] text-gray-400 mt-1">Estimate from the rolling window; retail spiff % is a shop setting.</p>
    </div>
  );
};

export default GrowthEarningsCard;
